/**
 * Create Club - Platform admin form for creating new clubs with auto-generated admin users
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import ErrorBanner from '../../components/common/ErrorBanner';
import { formatApiError } from '../../utils/errorFormatter';
import DomainProvisioningCard from '../../components/DomainProvisioningCard';
import useDomainPolling from '../../hooks/useDomainPolling';
import {
  Button,
  FormInput,
  FormSelect,
  FormTextArea,
  FormCheckbox,
  AdminPageLayout,
  Section,
  Card,
  CardContent,
  Grid,
  Col,
  Badge
} from '../../components';
import {
  AttributeGrid,
  AttributeStack,
  FormActionGroup,
  SuccessHero
} from '../../components/admin/patterns';
import { User, ClipboardList, CheckCircle, AlertTriangle } from 'lucide-react';

// Helper function to generate slug from name (hyphen-free)
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '')  // Remove special characters AND hyphens
    .replace(/\s+/g, '')          // Remove all spaces (no hyphens)
    .replace(/^-|-$/g, '');       // Remove any stray hyphens
};

const CreateClub = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';

    return {
      // Basic club information (hierarchical schema)
      slug: isDevelopment ? 'metroyouth' : '',
      legal_name: isDevelopment ? 'Metro Youth Soccer Club LLC' : '',
      display_name: isDevelopment ? 'Metro YSC' : '',
      description: isDevelopment ? 'A premier youth soccer club focused on developing young talent in a supportive environment.' : '',
      contact_email: isDevelopment ? 'info@metroysc.com' : '',
      contact_phone: isDevelopment ? '(555) 123-4567' : '',

      // Optional fields
      custom_domain: '',
      club_type: 'soccer_club',
      website: isDevelopment ? 'https://www.metroysc.com' : '',

      // Physical address
      address_line1: isDevelopment ? '123 Soccer Field Lane' : '',
      address_line2: isDevelopment ? 'Suite 200' : '',
      city: isDevelopment ? 'Springfield' : '',
      state: isDevelopment ? 'CA' : '',
      zip_code: isDevelopment ? '12345' : '',
      country: 'US',

      // Business information for invoicing
      business_registration_number: isDevelopment ? 'LLC-2024001' : '',
      tax_id: isDevelopment ? '12-3456789' : '',
      vat_number: isDevelopment ? 'VAT-123456789' : '',

      // Billing address (optional, defaults to physical)
      billing_address_line1: '',
      billing_address_line2: '',
      billing_city: '',
      billing_state: '',
      billing_zip_code: '',
      billing_country: 'US',
      same_as_physical: true,

      // Financial settings
      preferred_payment_method: 'bank_transfer',
      payment_terms: 30,
      currency: 'USD',
      timezone: 'America/New_York',

      // Admin user information
      admin_first_name: isDevelopment ? 'John' : '',
      admin_last_name: isDevelopment ? 'Smith' : '',
      admin_email: isDevelopment ? 'admin@metroysc.com' : '',
      admin_phone: isDevelopment ? '(555) 987-6543' : '',
      admin_password: isDevelopment ? 'TempPass123!' : ''
    };
  });

  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };

      // Handle billing address same as physical
      if (name === 'same_as_physical' && newValue) {
        updated.billing_address_line1 = updated.address_line1;
        updated.billing_address_line2 = updated.address_line2;
        updated.billing_city = updated.city;
        updated.billing_state = updated.state;
        updated.billing_zip_code = updated.zip_code;
        updated.billing_country = updated.country;
      } else if (name === 'same_as_physical' && !newValue) {
        // Clear billing address when unchecking
        updated.billing_address_line1 = '';
        updated.billing_address_line2 = '';
        updated.billing_city = '';
        updated.billing_state = '';
        updated.billing_zip_code = '';
        updated.billing_country = 'US';
      }

      // Auto-generate slug from legal_name if not manually set
      if (name === 'legal_name' && value) {
        // Only auto-generate if slug is empty or was previously auto-generated
        const currentSlugIsAutoGenerated = !prev.slug || prev.slug === generateSlug(prev.legal_name);
        if (currentSlugIsAutoGenerated) {
          updated.slug = generateSlug(value);
        }
      }

      return updated;
    });

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic club information validation (hierarchical schema required fields)
    if (!formData.slug.trim()) newErrors.slug = 'Club slug is required';
    if (!formData.legal_name.trim()) newErrors.legal_name = 'Legal business name is required';
    if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
    if (!formData.contact_phone.trim()) newErrors.contact_phone = 'Contact phone is required';

    // Admin user validation
    if (!formData.admin_first_name.trim()) newErrors.admin_first_name = 'Admin first name is required';
    if (!formData.admin_last_name.trim()) newErrors.admin_last_name = 'Admin last name is required';
    if (!formData.admin_email.trim()) newErrors.admin_email = 'Admin email is required';
    if (!formData.admin_password.trim()) newErrors.admin_password = 'Admin password is required';

    // Slug validation (lowercase alphanumeric only, NO hyphens)
    const slugRegex = /^[a-z0-9]+$/;
    if (formData.slug && !slugRegex.test(formData.slug)) {
      newErrors.slug = 'Slug must be lowercase letters and numbers only (no hyphens, spaces, or special characters)';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.contact_email && !emailRegex.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid contact email address';
    }
    if (formData.admin_email && !emailRegex.test(formData.admin_email)) {
      newErrors.admin_email = 'Please enter a valid admin email address';
    }

    // Password strength
    if (formData.admin_password && formData.admin_password.length < 8) {
      newErrors.admin_password = 'Password must be at least 8 characters';
    }

    setFieldErrors(newErrors);

    // Convert to error messages array for ErrorBanner
    const errorMessages = Object.entries(newErrors).map(([field, message]) => message);
    setErrors(errorMessages);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors([]);
    setFieldErrors({});
    setSuccess(null);

    try {
      // Prepare data to match hierarchical schema
      const clubData = {
        // Required fields
        slug: formData.slug,
        legal_name: formData.legal_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        address_line1: formData.address_line1,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,

        // Optional fields
        custom_domain: formData.custom_domain || '',
        display_name: formData.display_name || '',
        description: formData.description || '',
        club_type: formData.club_type,
        website: formData.website || '',
        address_line2: formData.address_line2 || '',
        country: formData.country,
        business_registration_number: formData.business_registration_number || '',
        tax_id: formData.tax_id || '',
        vat_number: formData.vat_number || '',
        billing_address_line1: formData.same_as_physical ? formData.address_line1 : (formData.billing_address_line1 || ''),
        billing_address_line2: formData.same_as_physical ? formData.address_line2 : (formData.billing_address_line2 || ''),
        billing_city: formData.same_as_physical ? formData.city : (formData.billing_city || ''),
        billing_state: formData.same_as_physical ? formData.state : (formData.billing_state || ''),
        billing_zip_code: formData.same_as_physical ? formData.zip_code : (formData.billing_zip_code || ''),
        billing_country: formData.same_as_physical ? formData.country : (formData.billing_country || 'US'),
        preferred_payment_method: formData.preferred_payment_method,
        payment_terms: formData.payment_terms,
        timezone: formData.timezone,
        currency: formData.currency,
        age_calculation_method: 'calendar_year', // Default
        settings: {},

        // Admin user
        admin_first_name: formData.admin_first_name,
        admin_last_name: formData.admin_last_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password
      };

      const result = await apiService.createClub(clubData);

      // DEBUG: Log the full response
      console.log('🎉 Club created successfully:', result);

      setSuccess({
        club: result.club,
        admin: result.admin_user,
        message: result.message,
        loginInstructions: result.login_instructions,
        domain_mapping: result.domain_mapping,  // ✅ NOW INCLUDED
        email_sent: result.email_sent
      });

      // Clear form
      const isDevelopment = process.env.NODE_ENV === 'development';
      setFormData({
        // Basic club information (hierarchical schema)
        slug: isDevelopment ? 'metro-youth' : '',
        legal_name: isDevelopment ? 'Metro Youth Soccer Club LLC' : '',
        display_name: isDevelopment ? 'Metro YSC' : '',
        description: isDevelopment ? 'A premier youth soccer club focused on developing young talent in a supportive environment.' : '',
        contact_email: isDevelopment ? 'info@metroysc.com' : '',
        contact_phone: isDevelopment ? '(555) 123-4567' : '',

        // Optional fields

        club_type: 'soccer_club',
        website: isDevelopment ? 'https://www.metroysc.com' : '',

        // Physical address
        address_line1: isDevelopment ? '123 Soccer Field Lane' : '',
        address_line2: isDevelopment ? 'Suite 200' : '',
        city: isDevelopment ? 'Springfield' : '',
        state: isDevelopment ? 'CA' : '',
        zip_code: isDevelopment ? '12345' : '',
        country: 'US',

        // Business information for invoicing
        business_registration_number: isDevelopment ? 'LLC-2024001' : '',
        tax_id: isDevelopment ? '12-3456789' : '',
        vat_number: isDevelopment ? 'VAT-123456789' : '',

        // Billing address (optional, defaults to physical)
        billing_address_line1: '',
        billing_address_line2: '',
        billing_city: '',
        billing_state: '',
        billing_zip_code: '',
        billing_country: 'US',
        same_as_physical: true,

        // Financial settings
        preferred_payment_method: 'bank_transfer',
        payment_terms: 30,
        currency: 'USD',
        timezone: 'America/New_York',

        // Admin user information
        admin_first_name: isDevelopment ? 'John' : '',
        admin_last_name: isDevelopment ? 'Smith' : '',
        admin_email: isDevelopment ? 'admin@metroysc.com' : '',
        admin_phone: isDevelopment ? '(555) 987-6543' : '',
        admin_password: isDevelopment ? 'TempPass123!' : ''
      });

    } catch (err) {
      // Format API errors into user-friendly messages
      const formattedErrors = formatApiError(err);
      setErrors(formattedErrors);

      // Scroll to top to show error banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, admin_password: password }));
  };

  return (
    <AdminPageLayout
      title={success ? "PROVISIONING COMPLETE" : "PROVISION ORGANIZATION"}
      subtitle={success ? "Global infrastructure setup successfully initiated" : "Initializing new soccer club entity and administrative nodes"}
      backPath="/admin/clubs"
      maxWidth="1400px"
    >
      {success ? (
        <DomainProvisioningWrapper success={success} />
      ) : (
        <form onSubmit={handleSubmit}>
          <ErrorBanner
            type="error"
            messages={errors}
            onDismiss={() => setErrors([])}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
            {/* 1. Club Identity & Core Configuration */}
            <Section title="ECOSYSTEM IDENTITY">
              <Card level={1}>
                <CardContent>
                  <Grid columns={12} gap="2.5rem">
                    <Col span={6}>
                      <FormInput
                        label="Legal Business Entity Name"
                        name="legal_name"
                        value={formData.legal_name}
                        onChange={handleInputChange}
                        error={fieldErrors.legal_name}
                        required={true}
                        placeholder="Metropolitan Athletic Soccer Club LLC"
                        helpText="Official registered nomenclature for the organization"
                      />
                    </Col>
                    <Col span={6}>
                      <FormInput
                        label="Public Display Identifier"
                        name="display_name"
                        value={formData.display_name}
                        onChange={handleInputChange}
                        error={fieldErrors.display_name}
                        required={false}
                        placeholder="Metro Soccer Academy"
                        helpText="Primary public-facing brand identity"
                      />
                    </Col>

                    <Col span={4}>
                      <FormInput
                        label="Organization Slug"
                        name="slug"
                        value={formData.slug}
                        onChange={handleInputChange}
                        error={fieldErrors.slug}
                        required={true}
                        placeholder="metroyouth"
                        helpText={
                          formData.slug
                            ? `GATEWAY: ${formData.slug}.${window.location.hostname}`
                            : "Lowercase letters and numbers only."
                        }
                      />
                    </Col>
                    <Col span={4}>
                      <FormInput
                        label="Custom Endpoint (Domain)"
                        name="custom_domain"
                        value={formData.custom_domain}
                        onChange={handleInputChange}
                        error={fieldErrors.custom_domain}
                        placeholder="academy.com"
                        helpText="Map a secondary top-level domain"
                      />
                    </Col>
                    <Col span={4}>
                      <FormSelect
                        label="Governance Model"
                        name="club_type"
                        value={formData.club_type}
                        onChange={handleInputChange}
                        error={fieldErrors.club_type}
                        required={true}
                        options={[
                          { value: 'soccer_club', label: 'Soccer Club' },
                          { value: 'youth_league', label: 'Youth League' },
                          { value: 'school_program', label: 'School Program' },
                          { value: 'academy', label: 'Academy' },
                          { value: 'community_center', label: 'Community Center' }
                        ]}
                      />
                    </Col>

                    <Col span={12}>
                      <FormTextArea
                        label="Mission & Strategic Rationale"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Brief overview of the organizational mission..."
                      />
                    </Col>

                    <Col span={6}>
                      <FormInput
                        label="Official Portal URL"
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder="https://www.officialportal.com"
                      />
                    </Col>
                    <Col span={6}>
                      <Grid columns={2} gap="1.5rem">
                        <FormInput
                          label="Administrative Email"
                          type="email"
                          name="contact_email"
                          value={formData.contact_email}
                          onChange={handleInputChange}
                          error={fieldErrors.contact_email}
                          required={true}
                          placeholder="ops@organization.com"
                        />
                        <FormInput
                          label="Administrative Hotline"
                          type="tel"
                          name="contact_phone"
                          value={formData.contact_phone}
                          onChange={handleInputChange}
                          error={fieldErrors.contact_phone}
                          required={true}
                          placeholder="+1 (555) 000-0000"
                        />
                      </Grid>
                    </Col>
                  </Grid>
                </CardContent>
              </Card>
            </Section>

            {/* 2. Primary Location */}
            <Section title="PHYSICAL LOCALIZATION">
              <Card level={1}>
                <CardContent>
                  <Grid columns={12} gap="2rem">
                    <Col span={12}>
                      <FormInput
                        label="Headquarters Physical Address"
                        name="address_line1"
                        value={formData.address_line1}
                        onChange={handleInputChange}
                        error={fieldErrors.address_line1}
                        required={true}
                        placeholder="Station Street and Suite Details"
                      />
                    </Col>
                    <Col span={5}>
                      <FormInput
                        label="City / Municipality"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        error={fieldErrors.city}
                        required={true}
                      />
                    </Col>
                    <Col span={3}>
                      <FormInput
                        label="State / Territory"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        error={fieldErrors.state}
                        required={true}
                      />
                    </Col>
                    <Col span={4}>
                      <FormInput
                        label="Registry Postcode"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        error={fieldErrors.zip_code}
                        required={true}
                      />
                    </Col>
                  </Grid>
                </CardContent>
              </Card>
            </Section>

            {/* 3. Business & Financials */}
            <Grid columns={2} gap="2.5rem">
              <Section title="TREASURY CONFIGURATION">
                <Card level={1} height="100%">
                  <CardContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      <FormInput
                        label="Entity Registration Number"
                        name="business_registration_number"
                        value={formData.business_registration_number}
                        onChange={handleInputChange}
                        placeholder="Commercial Registry ID"
                      />
                      <FormInput
                        label="Tax / Fiscal Identifier"
                        name="tax_id"
                        value={formData.tax_id}
                        onChange={handleInputChange}
                        placeholder="Tax ID or EIN"
                      />
                      <FormSelect
                        label="Primary Settlement Method"
                        name="preferred_payment_method"
                        value={formData.preferred_payment_method}
                        onChange={handleInputChange}
                        options={[
                          { value: 'bank_transfer', label: 'Electronic Bank Transfer' },
                          { value: 'credit_card', label: 'Enterprise Card' },
                          { value: 'check', label: 'Physical Instrument (Check)' }
                        ]}
                      />
                      <Grid columns={2} gap="1.5rem">
                        <FormInput
                          label="Settlement Terms (Days)"
                          type="number"
                          name="payment_terms"
                          value={formData.payment_terms}
                          onChange={handleInputChange}
                          error={fieldErrors.payment_terms}
                        />
                        <FormSelect
                          label="Operational Currency"
                          name="currency"
                          value={formData.currency}
                          onChange={handleInputChange}
                          options={[
                            { value: 'USD', label: 'USD - US Dollar' },
                            { value: 'EUR', label: 'EUR - Euro' },
                            { value: 'GBP', label: 'GBP - British Pound' }
                          ]}
                        />
                      </Grid>
                    </div>
                  </CardContent>
                </Card>
              </Section>

              <Section title="ADMIN REGISTRY INITIALIZATION">
                <Card level={1} height="100%">
                  <CardContent>
                    <div style={{ background: 'var(--surface-lvl2)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--color-border-light)', marginBottom: '2rem' }}>
                      <FormCheckbox
                        label="Billing parameters mirror physical site"
                        name="same_as_physical"
                        checked={formData.same_as_physical}
                        onChange={handleInputChange}
                      />
                    </div>

                    {!formData.same_as_physical && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
                        <FormInput
                          label="Billing Disbursement Address"
                          name="billing_address_line1"
                          value={formData.billing_address_line1}
                          onChange={handleInputChange}
                          error={fieldErrors.billing_address_line1}
                          required={true}
                        />
                        <Grid columns={3} gap="1rem">
                          <FormInput label="City" name="billing_city" value={formData.billing_city} onChange={handleInputChange} required={true} />
                          <FormInput label="State" name="billing_state" value={formData.billing_state} onChange={handleInputChange} required={true} />
                          <FormInput label="ZIP" name="billing_zip_code" value={formData.billing_zip_code} onChange={handleInputChange} required={true} />
                        </Grid>
                      </div>
                    )}

                    <div style={{ marginTop: '0', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border-light)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                        <User size={22} style={{ color: 'var(--color-primary)' }} />
                        <h4 style={{ margin: 0, fontSize: '1.1rem' }}>PRIMARY GUARDIAN NODE</h4>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <Grid columns={2} gap="1.5rem">
                          <FormInput label="Given Name" name="admin_first_name" value={formData.admin_first_name} onChange={handleInputChange} error={fieldErrors.admin_first_name} required={true} />
                          <FormInput label="Family Name" name="admin_last_name" value={formData.admin_last_name} onChange={handleInputChange} error={fieldErrors.admin_last_name} required={true} />
                        </Grid>
                        <FormInput label="Authentication Email" type="email" name="admin_email" value={formData.admin_email} onChange={handleInputChange} error={fieldErrors.admin_email} required={true} />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', opacity: 0.5, letterSpacing: '0.05em' }}>INITIAL ACCESS CREDENTIAL *</label>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="text"
                              name="admin_password"
                              value={formData.admin_password}
                              onChange={handleInputChange}
                              style={{
                                flex: 1,
                                padding: '0.75rem 1rem',
                                background: 'var(--surface-lvl3)',
                                border: `1px solid ${fieldErrors.admin_password ? 'var(--color-error)' : 'var(--color-border)'}`,
                                borderRadius: '10px',
                                color: 'white',
                                fontFamily: 'monospace',
                                fontSize: '1rem'
                              }}
                            />
                            <Button type="button" onClick={generatePassword} variant="outline" size="md">GENERATE</Button>
                          </div>
                          {fieldErrors.admin_password && <span style={{ color: 'var(--color-error)', fontSize: '0.75rem', fontWeight: 600 }}>{fieldErrors.admin_password}</span>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Section>
            </Grid>

            <FormActionGroup
              onCancel={() => navigate('/admin/clubs')}
              onSave={handleSubmit}
              loading={loading}
              saveLabel="Save"
              cancelLabel="Cancel"
              isEdit={false}
            />
          </div>
        </form>
      )}
    </AdminPageLayout>
  );
};

/**
 * Domain Provisioning Wrapper Component
 * Handles domain polling and displays status card
 */
function DomainProvisioningWrapper({ success }) {
  const navigate = useNavigate();

  const domainData = success?.domain_mapping || {};
  const domain = domainData.domain || '';
  const domainId = domainData.id || '';
  const club_id = success?.club?.tenant_id || '';

  const {
    status,
    sslStatus,
    instructions,
    dnsRecords,
    error,
    isPolling,
    retry,
  } = useDomainPolling(club_id, domainId, {
    interval: 5000,
    autoStart: true,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
      <SuccessHero
        title="Organization Created"
        subtitle={`The global resource architecture for ${success.club.legal_name} is currently being initialized across our high-availability network nodes.`}
        icon={<CheckCircle />}
        actions={
          <>
            <Button variant="outline" size="lg" onClick={() => navigate('/admin/clubs')}>RETURN TO CLUB MANAGEMENT</Button>
            <Button variant="primary" size="lg" onClick={() => window.location.reload()}>ADD ANOTHER CLUB</Button>
          </>
        }
      />

      <Grid columns={12} gap="2.5rem">
        <Col span={6}>
          <Section title="ENTITY REGISTRY DETAILS">
            <Card level={1} height="100%">
              <CardContent>
                <AttributeGrid columns={1} gap="1.5rem">
                  <AttributeStack label="Legal Entity" value={success.club.legal_name} />
                  <AttributeStack
                    label="Model Type"
                    value={
                      <Badge variant="outline">{success.club.club_type?.replace('_', ' ').toUpperCase()}</Badge>
                    }
                  />
                  <AttributeStack label="Tenant ID" value={<code>{success.club.tenant_id}</code>} />
                  <AttributeStack label="Physical Node" value={`${success.club.city.toUpperCase()}, ${success.club.state.toUpperCase()}`} />
                </AttributeGrid>
              </CardContent>
            </Card>
          </Section>
        </Col>

        <Col span={6}>
          <Section title="GUARDIANSHIP CREDENTIALS">
            <Card level={1} height="100%">
              <CardContent>
                <AttributeGrid columns={1} gap="1.5rem">
                  <AttributeStack label="Primary Guardian" value={`${success.admin.first_name.toUpperCase()} ${success.admin.last_name.toUpperCase()}`} />
                  <AttributeStack label="Access Endpoint" value={success.admin.email} />
                  <AttributeStack
                    label="Security Level"
                    value={<Badge variant="primary">CLUB ADMINISTRATOR</Badge>}
                  />
                  <AttributeStack
                    label="Network Status"
                    value={<Badge variant="success">ACTIVE NODE</Badge>}
                  />
                </AttributeGrid>
              </CardContent>
            </Card>
          </Section>
        </Col>
      </Grid>

      {(domain || domainData.status) && (
        <Section title="NETWORK INFRASTRUCTURE PROPAGATION">
          <Card level={1}>
            <CardContent>
              <DomainProvisioningCard
                domain={domain}
                status={status}
                sslStatus={sslStatus}
                instructions={instructions}
                dnsRecords={dnsRecords}
                isLoading={isPolling}
                error={error}
                onRetry={retry}
                showDetails={true}
              />
            </CardContent>
          </Card>
        </Section>
      )}

      <Section title="POST-PROVISIONING PROTOCOL">
        <Card level={1} variant="feature" style={{ background: 'var(--surface-lvl2)', border: '1px solid var(--color-border-light)' }}>
          <CardContent>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-start' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'var(--surface-lvl3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-warning)',
                border: '1px solid var(--color-border-light)'
              }}>
                <ClipboardList size={32} />
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: 800 }}>SECURITY HANDOVER & INITIALIZATION</h4>
                <p style={{ opacity: 0.8, lineHeight: '1.8', margin: 0, fontSize: '1.05rem' }}>
                  {success.loginInstructions}
                </p>
                <div style={{ marginTop: '2rem', padding: '1.25rem', background: 'var(--surface-lvl3)', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                  <p style={{ opacity: 0.6, lineHeight: '1.6', margin: 0, fontSize: '0.9rem', fontWeight: 500 }}>
                    <AlertTriangle size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle', color: 'var(--color-warning)' }} />
                    Global DNS propagation and SSL certificate issuance are autonomous processes. These may take up to 60 minutes to finalize across all worldwide geographic points of presence.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>
    </div>
  );
}

export default CreateClub;